import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography, Alert } from "@mui/material";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import AddIcon from "@mui/icons-material/Add";
import Pagination from "../../Pagination"; // Assuming your custom Pagination component
import { useAuth } from "../../utils/AuthContext";
import MyAdsList from "./MyAdsList";
import { Helmet } from "react-helmet-async";
import AnalyticsTracker from "../../GASetup/AnalyticsTracker";
import LoadingAnimation from "../../Animations/LoadingAnimation";
import {
  headingFonts,
  mainButtonStyles,
  notFoundBox,
} from "../../../Styles/Styles";
import DeletePromptModal from "./Modals/DeletePromptModal";
import SoldStatusPromptModal from "./Modals/SoldStatusPromptModal";
import AdStatusModal from "./Modals/AdStatusModal";

const MyAds = () => {
  const [ads, setAds] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteStatusModal, setDeleteStatusModal] = useState({
    open: false,
    type: "",
    message: "",
  });
  const [soldStatusModal, setSoldStatusModal] = useState({
    open: false,
    type: "",
    message: "",
  });
  const [selectedAdId, setSelectedAdId] = useState(null); //for tracking which ad to delete/sold/not sold
  const [soldModal, setSoldModal] = useState(false);
  const [markAsSoldValue, setMarkAsSoldValue] = useState(null);

  const { logout } = useAuth();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const fetchAds = async (page = 1) => {
      if (!user?.email) {
        setError("User not found. Please log in.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/myads`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email: user.email, page, limit: 10 }),
          }
        );

        if (response.status === 401) {
          alert("Unauthorized. Please log in.");
          setLoading(false);
          logout();
        } else if (!response.ok) {
          setLoading(false);
        }

        const data = await response.json();
        setAds(data.ads);
        setTotalPages(data.totalPages);
        setLoading(false);
      } catch (error) {
        setError(error.message);
        setLoading(false);
      }
    };

    fetchAds(currentPage);
  }, [user?.email, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleEdit = (uniqueId) => {
    navigate(`/edit-ad/${uniqueId}`);
  };

  const handleDeleteClick = (adId) => {
    setSelectedAdId(adId);
    setDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setDeleteModal(false);
    setSelectedAdId(null);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/delete/${selectedAdId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete ad");
      }

      setAds(ads.filter((ad) => ad._id !== selectedAdId));

      // Show success modal
      setDeleteStatusModal({
        open: true,
        type: "success",
        message: "Your ad has been successfully deleted.",
        adID: selectedAdId,
      });
    } catch (error) {
      setError(error.message);
      //  Show error modal
      setDeleteStatusModal({
        open: true,
        type: "error",
        message: error.message || "Something went wrong while deleting the ad.",
        adID: selectedAdId,
      });
    } finally {
      setDeleteModal(false);
      setSelectedAdId(null);
    }
  };

  const handleSoldClick = (uniqueId, isSold) => {
    setSelectedAdId(uniqueId);
    setMarkAsSoldValue(!isSold); // if ad isSold, then next action = mark as NOT sold
    setSoldModal(true);
    console.log(isSold);
  };

  const handleCancelSold = () => {
    setSoldModal(false);
    setSelectedAdId(null);
    setMarkAsSoldValue(null);
  };

  const handleConfirmSold = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/mark-as-sold/${selectedAdId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ markAsSoldValue }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update ad status");
      }
      const updatedAd = await response.json();
      setAds(
        ads.map((ad) =>
          ad.uniqueId === selectedAdId ? { ...ad, isSold: markAsSoldValue } : ad
        )
      );
      setSoldStatusModal({
        open: true,
        type: "success",
        message: `Your ad has been marked as ${
          updatedAd.isSold ? "Sold" : "Not Sold"
        }.`,
      });
    } catch (error) {
      setError(error.message);
      setSoldStatusModal({
        open: true,
        type: "error",
        message: error.message || "Something went wrong while updating status.",
      });
    } finally {
      setSoldModal(false);
      setSelectedAdId(null);
      setMarkAsSoldValue(null);
    }
  };

  const handlePostAd = () => {
    navigate("/post-ad");
  };

  const handleViewOffers = (uniqueId) => {
    navigate(`/offers/${uniqueId}`);
  };

  const handleViewAd = (uniqueId) => {
    navigate(`/ad/${uniqueId}`);
  };

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Helmet>
        <title>My Ads - Listed.ky</title>
        <meta name="description" content="My Ads - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4" sx={{ ...headingFonts }}>
          My Ads
        </Typography>
        <Button
          onClick={handlePostAd}
          className="main-button-style"
          sx={{ ...mainButtonStyles }}
        >
          <>
            <span>POST AD</span>
            <AddIcon />
          </>
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <>
        {ads ? (
          <MyAdsList
            ads={ads}
            handleViewAd={handleViewAd}
            handleViewOffers={handleViewOffers}
            handleEdit={handleEdit}
            deleteModal={deleteModal}
            handleDeleteClick={handleDeleteClick}
            handleCancelDelete={handleCancelDelete}
            handleConfirmDelete={handleConfirmDelete}
            soldModal={soldModal}
            handleSoldClick={handleSoldClick}
            markAsSoldValue={markAsSoldValue}
            handleConfirmSold={handleConfirmSold}
            handleCancelSold={handleCancelSold}
          />
        ) : (
          <Box sx={{ ...notFoundBox }}>
            <SearchOffOutlinedIcon
              sx={{
                color: "var(--primary-color)",
                fontSize: 50,
                opacity: 0.5,
                marginBottom: 1,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: "var(--primary-color)",
                fontFamily: "ClashGrotesk-Medium",
                letterSpacing: 1,
              }}
            >
              No ads found.
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              To post ad, use Post Ad button above.
            </Typography>
          </Box>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ mt: 6 }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </Box>
        )}
      </>
      <DeletePromptModal
        open={deleteModal}
        message="Do you really want to delete this Ad? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      <SoldStatusPromptModal
        open={soldModal}
        message={
          markAsSoldValue
            ? "Do you really want to mark this ad as SOLD?"
            : "Do you really want to mark this ad as NOT SOLD?"
        }
        onConfirm={handleConfirmSold}
        onCancel={handleCancelSold}
      />
      <AdStatusModal
        open={deleteStatusModal.open}
        type={deleteStatusModal.type}
        message={deleteStatusModal.message}
        setStatusModal={setDeleteStatusModal}
      />
      <AdStatusModal
        open={soldStatusModal.open}
        type={soldStatusModal.type}
        message={soldStatusModal.message}
        setStatusModal={setSoldStatusModal}
      />
    </Box>
  );
};

export default MyAds;
